import { useCallback, useId } from "react"
import { PublicOutlined } from "@mui/icons-material"
import { Box, Divider, Switch, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import type { NetworkAccessChange, NetworkAccessSettings } from "resources/ai-settings"
import { AllowedDomainsSection } from "./allowed-domains-section"
import { BODY_SX, CARD_SX, HEADER_SX, MUTED_SX, TEXT_SX, TITLE_SX } from "./card-styles"

const SWITCH_SX = { flexShrink: 0 } satisfies SxProps<Theme>

export interface NetworkAccessCardProps {
  settings: NetworkAccessSettings
  isSaving: boolean
  onChange: (change: NetworkAccessChange) => Promise<boolean>
}

export function NetworkAccessCard({ settings, isSaving, onChange }: NetworkAccessCardProps) {
  const webSearchTitleId = useId()
  const webSearchDescriptionId = useId()
  const domainAccessTitleId = useId()

  const changeWebSearch = useCallback((_: unknown, checked: boolean) => {
    if (!isSaving) void onChange({ setting: "webSearch", to: checked })
  }, [isSaving, onChange])

  const changeAllowedHosts = useCallback((to: string[]) => onChange({ setting: "allowedHosts", to }), [onChange])

  return (
    <Box sx={CARD_SX} data-testid="network-access-card" aria-busy={isSaving}>
      <Box sx={HEADER_SX}>
        <PublicOutlined fontSize="small" />
        <Typography component="h3" variant="subtitle1" sx={TITLE_SX}>Network access</Typography>
      </Box>
      <Box sx={BODY_SX}>
        <Box sx={TEXT_SX}>
          <Typography id={webSearchTitleId} variant="body1" sx={TITLE_SX}>Web search</Typography>
          <Typography id={webSearchDescriptionId} variant="body2" sx={MUTED_SX}>
            Let Kelly search the web and fetch pages from the domains allowed below.
          </Typography>
        </Box>
        <Switch
          checked={settings.webSearch}
          // Not `disabled`: disabling the focused switch drops keyboard focus out of the dock.
          onChange={changeWebSearch}
          slotProps={{ input: { "aria-labelledby": webSearchTitleId, "aria-describedby": webSearchDescriptionId } }}
          sx={SWITCH_SX}
        />
      </Box>
      <Divider />
      <Box sx={BODY_SX}>
        <Box sx={TEXT_SX}>
          <Typography id={domainAccessTitleId} variant="body1" sx={TITLE_SX}>Domain access</Typography>
          <Typography variant="body2" sx={MUTED_SX}>
            Kelly can search, fetch pages and run code that connects only to the domains you allow.
          </Typography>
        </Box>
      </Box>
      <AllowedDomainsSection
        allowedHosts={settings.allowedHosts}
        lockedHosts={settings.lockedHosts}
        suggestedHosts={settings.suggestedHosts}
        isSaving={isSaving}
        labelledBy={domainAccessTitleId}
        onAllowedHostsChange={changeAllowedHosts}
      />
    </Box>
  )
}
